import React from 'react';
import { format } from 'date-fns';
import { contentLabels } from '../config/contentLabels';
import type { PostSummary } from '../api/types';
import SiteHeader from './SiteHeader';

interface BlogListProps {
  posts: PostSummary[];
  loading: boolean;
  onPostSelect: (slug: string) => void;
  onAdminClick?: () => void;
}

function displayDate(post: PostSummary): string {
  const raw = post.date || post.createdAt;
  try {
    return format(new Date(raw), 'MMM dd, yyyy');
  } catch {
    return '';
  }
}

const BlogList: React.FC<BlogListProps> = ({ posts, loading, onPostSelect, onAdminClick }) => {
  const articles = posts.filter((p) => p.kind === 'article');
  const shorts = posts.filter((p) => p.kind === 'short');

  return (
    <div className="blog-list">
      <SiteHeader showTagline onAdminClick={onAdminClick} />

      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <>
          <section className="content-section" aria-labelledby="writing-heading">
            <h2 id="writing-heading" className="content-section-title">
              {contentLabels.longFormSection}
            </h2>
            <div className="posts-grid">
              {articles.length === 0 && (
                <p className="empty-state">No posts yet.</p>
              )}
              {articles.map((post) => (
                <article
                  key={post.slug}
                  className={`post-card${!post.published ? ' post-card--draft' : ''}`}
                  onClick={() => onPostSelect(post.slug)}
                >
                  <div className="post-card-content">
                    {!post.published && <span className="draft-badge">Draft</span>}
                    <h3 className="post-title">{post.title}</h3>
                    <div className="post-meta">
                      <span className="post-date">{displayDate(post)}</span>
                      {post.author && (
                        <span className="post-author">by {post.author}</span>
                      )}
                    </div>
                    {post.summary && (
                      <p className="post-excerpt">{post.summary}</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="content-section thoughts-section" aria-labelledby="thoughts-heading">
            <h2 id="thoughts-heading" className="content-section-title">
              {contentLabels.shortFormSection}
            </h2>
            <div className="thoughts-stream">
              {shorts.length === 0 && (
                <p className="empty-state">No thoughts yet.</p>
              )}
              {shorts.map((post) => (
                <article
                  key={post.slug}
                  className="thought-card"
                  onClick={() => onPostSelect(post.slug)}
                >
                  <div className="thought-card-inner">
                    <div className="thought-meta">
                      <span className="thought-date">{displayDate(post)}</span>
                      {post.title && post.title !== 'Thought' && (
                        <span className="thought-title-inline">{post.title}</span>
                      )}
                    </div>
                    {post.summary && (
                      <p className="thought-body-preview">{post.summary}</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default BlogList;
